/**
 * POS-I109 — the client-side session boundary.
 *
 * Everything the browser keeps for a signed-in user (React-Query cache, the service worker's API
 * caches, the offline IndexedDB catalogue, persisted stores and localStorage keys with store
 * data, the in-memory cart, in-flight requests) belongs to ONE owner: `<tenantId>:<userId>`.
 * Whenever the owner changes — login, logout, token-refresh failure, tenant switch, impersonation
 * start/stop, or another tab signing in as someone else — `resetClientSession()` runs once and:
 *   1. bumps the session epoch, so responses of requests sent under the old owner are dropped
 *      (see the interceptors in `lib/api.ts`), and aborts every in-flight API request;
 *   2. cancels and clears React-Query;
 *   3. removes the persisted keys that hold store data (draft cart + customer, the customer
 *      display state, the selected location) and resets the in-memory cart and location;
 *   4. deletes the service worker's API caches;
 *   5. closes the offline DB handle and drops the previous owner's cached catalogue (queued
 *      sales stay in that owner's own database).
 *
 * The owner is also persisted (`clearring-session-owner`), so a boundary that was cut short by a
 * page reload (e.g. the 401 path sets `window.location.href`) is completed on the next boot.
 */
import { useAuthStore } from '@/stores/auth.store';
import { useLocationStore } from '@/stores/location.store';
import { useCartStore } from '@/stores/cart.store';
import { queryClient } from '@/lib/query-client';
import { clearOfflineCacheFor, closeOfflineDb, migrateLegacyOfflineDb } from '@/lib/offline-db';

export const SESSION_OWNER_KEY = 'clearring-session-owner';

/** localStorage keys that hold one store's / user's data and must not outlive the session. */
export const TENANT_LOCAL_STORAGE_KEYS = [
  'pos-draft-cart',
  'pos-draft-customer',
  'pos-cfd-state',
  'clearring-location-ctx',
] as const;

/** Service-worker runtime caches that held `/api/v1/` responses (see vite.config.ts). */
export const API_CACHE_NAMES = ['pos-api-cache', 'api-cache'] as const;

type Owner = { tenantId: string; userId: string };

let epoch = 0;
let controller = new AbortController();
let currentOwnerKey: string | null = null;
let initialised = false;

export function ownerKeyOf(tenantId?: string | null, userId?: string | null): string | null {
  return tenantId && userId ? `${tenantId}:${userId}` : null;
}

function parseOwner(key: string | null): Owner | null {
  if (!key) return null;
  const i = key.indexOf(':');
  if (i <= 0) return null;
  return { tenantId: key.slice(0, i), userId: key.slice(i + 1) };
}

/** Current session epoch; a request stamped with an older epoch belongs to a previous owner. */
export function getSessionEpoch(): number {
  return epoch;
}

/** Abort signal shared by every API request of the current owner. */
export function getSessionSignal(): AbortSignal {
  return controller.signal;
}

function safeLocalStorage(fn: (ls: Storage) => void) {
  try {
    if (typeof localStorage !== 'undefined') fn(localStorage);
  } catch {
    /* storage blocked — nothing persisted to clear */
  }
}

/**
 * The one function that ends a client session. Synchronous part first (so a page navigation
 * right after it cannot leave data behind), then the async caches.
 */
export function resetClientSession(previousOwnerKey: string | null, nextOwnerKey: string | null): Promise<void> {
  epoch += 1;
  controller.abort();
  controller = new AbortController();

  void queryClient.cancelQueries();
  queryClient.clear();

  safeLocalStorage((ls) => {
    for (const k of TENANT_LOCAL_STORAGE_KEYS) ls.removeItem(k);
    if (nextOwnerKey) ls.setItem(SESSION_OWNER_KEY, nextOwnerKey);
    else ls.removeItem(SESSION_OWNER_KEY);
  });
  useLocationStore.setState({ selectedLocationId: null, selectedLocationName: null });
  useCartStore.getState().clearCart();

  currentOwnerKey = nextOwnerKey;
  return clearAsyncCaches(previousOwnerKey);
}

async function clearAsyncCaches(previousOwnerKey: string | null): Promise<void> {
  const tasks: Promise<unknown>[] = [];
  if (typeof caches !== 'undefined') {
    for (const name of API_CACHE_NAMES) tasks.push(caches.delete(name).catch(() => false));
  }
  tasks.push(closeOfflineDb());
  const prev = parseOwner(previousOwnerKey);
  if (prev) tasks.push(clearOfflineCacheFor(prev.tenantId, prev.userId).catch(() => undefined));
  await Promise.all(tasks);
}

function ownerFromStore(): string | null {
  const { tenant, user } = useAuthStore.getState();
  return ownerKeyOf(tenant?.id, user?.id);
}

/**
 * Wire the boundary once at boot (main.tsx): finish a boundary a reload cut short, move the
 * pre-POS-I109 shared offline DB, then reset on every owner change in this tab or another.
 */
export function initSessionBoundary(): void {
  if (initialised) return;
  initialised = true;

  let stored: string | null = null;
  safeLocalStorage((ls) => {
    stored = ls.getItem(SESSION_OWNER_KEY);
  });
  const now = ownerFromStore();
  currentOwnerKey = now;
  if (stored !== now) {
    void resetClientSession(stored, now);
  } else if (typeof caches !== 'undefined') {
    // The service worker no longer caches API responses; drop what an older build stored.
    for (const name of API_CACHE_NAMES) void caches.delete(name).catch(() => false);
  }
  const owner = parseOwner(now);
  if (owner) void migrateLegacyOfflineDb(owner.tenantId, owner.userId).catch(() => 0);

  useAuthStore.subscribe((state) => {
    const next = ownerKeyOf(state.tenant?.id, state.user?.id);
    if (next === currentOwnerKey) return;
    const previous = currentOwnerKey;
    void resetClientSession(previous, next).then(() => {
      const o = parseOwner(next);
      if (o) return migrateLegacyOfflineDb(o.tenantId, o.userId).catch(() => 0);
      return 0;
    });
  });

  // Another tab signed in / out: pick up its session so this tab never shows the old owner.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
      if (e.key === 'clearring-auth' || e.key === null) {
        void useAuthStore.persist.rehydrate();
      }
    });
  }
}

let leavingToLogin = false;

/**
 * Sign the user out and leave with a FULL page navigation, so no in-memory state (module
 * variables, window markers, mounted components) survives into the next session. `logout()`
 * changes the owner, which runs the synchronous part of the boundary before the navigation.
 * Runs once: later callers (a second 401, the logout mutation's two paths) are ignored.
 */
export function endSessionToLogin(): void {
  if (leavingToLogin) return;
  leavingToLogin = true;
  useAuthStore.getState().logout();
  if (typeof window !== 'undefined') window.location.replace('/login');
}

/** Test hook: forget wiring between specs. */
export function __resetSessionBoundaryForTests(): void {
  leavingToLogin = false;
  initialised = false;
  currentOwnerKey = null;
  epoch = 0;
  controller = new AbortController();
}
